import React from "react";
import { FiChevronDown, FiEdit3, FiEye, FiGrid, FiHelpCircle, FiList, FiMaximize2, FiPlus, FiRefreshCw, FiSave, FiTrash2 } from "react-icons/fi";
import { monthDays } from "../data/hrmMockData";
import ComponentLoader from "./ComponentLoader";

const REQUIRED_DAILY_TIMESHEET_HOURS = 8;

const getEntryHours = (entry) => {
  if (entry?.items?.length) {
    return entry.items.reduce((sum, item) => sum + Number(item.hours || 0), 0);
  }
  return Number(entry?.hours || 0);
};

const normalizeProjectColor = (value) => {
  const color = String(value || "").trim();
  return /^#[0-9a-fA-F]{6}$/.test(color) ? color : "";
};

const getEntryProjectColor = (entry) => {
  const colorSource = entry?.items?.find((item) =>
    item.projectColor || item.projectColour || item.color || item.colour
  ) || entry || {};
  return normalizeProjectColor(
    colorSource.projectColor || colorSource.projectColour || colorSource.color || colorSource.colour
  );
};

const formatLoggedHours = (entry) => {
  if (entry?.isOutsideMonth || !entry?.workDate) {
    return "";
  }
  const hours = getEntryHours(entry);
  return hours ? String(Number(hours.toFixed(2))) : "-";
};

const formatWorkDateDay = (entry) => {
  if (entry?.isOutsideMonth || !entry?.workDate) {
    return "";
  }
  const isoDateParts = String(entry.workDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!isoDateParts) {
    return "";
  }
  const monthLabels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const monthLabel = monthLabels[Number(isoDateParts[2]) - 1];
  const dayNumber = Number(isoDateParts[3]);
  return Number.isFinite(dayNumber) && monthLabel ? `${dayNumber}-${monthLabel}` : "";
};

const MONTH_LOOKUP = {
  jan: "01",
  january: "01",
  feb: "02",
  february: "02",
  mar: "03",
  march: "03",
  apr: "04",
  april: "04",
  may: "05",
  jun: "06",
  june: "06",
  jul: "07",
  july: "07",
  aug: "08",
  august: "08",
  sep: "09",
  sept: "09",
  september: "09",
  oct: "10",
  october: "10",
  nov: "11",
  november: "11",
  dec: "12",
  december: "12",
};

const toDateKey = (value) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) {
    return "";
  }
  const isoMatch = rawValue.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoMatch) {
    return isoMatch[1];
  }
  const displayDateMatch = rawValue.match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/);
  const matchedMonth = displayDateMatch ? MONTH_LOOKUP[displayDateMatch[2].toLowerCase()] : "";
  if (matchedMonth) {
    return `${displayDateMatch[3]}-${matchedMonth}-${String(displayDateMatch[1]).padStart(2, "0")}`;
  }
  const parsedDate = new Date(rawValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }
  return `${parsedDate.getFullYear()}-${String(parsedDate.getMonth() + 1).padStart(2, "0")}-${String(parsedDate.getDate()).padStart(2, "0")}`;
};

const getTodayDateKey = () => toDateKey(new Date());

const isWeekendWorkDate = (value) => {
  const dateKey = toDateKey(value);
  if (!dateKey) {
    return false;
  }
  const date = new Date(`${dateKey}T00:00:00`);
  return !Number.isNaN(date.getTime()) && (date.getDay() === 0 || date.getDay() === 6);
};

const getEntryStatus = (entry, month) => entry?.status || (month?.status === "Approved" ? "Approved" : "Draft");

const getStatusLabel = (status) => {
  if (status === "Submitted") {
    return "In Review";
  }
  return status || "Draft";
};

const formatStatusWithHours = (status, entry) => {
  const label = getStatusLabel(status);
  const hours = formatLoggedHours(entry);
  return hours && hours !== "-" ? `${label} (${hours})` : label;
};

const getStatusChipClass = (status) => {
  if (status === "Submitted") {
    return "new-hrm-chip--amber";
  }
  if (status === "Approved") {
    return "new-hrm-chip--green";
  }
  if (status === "Rejected") {
    return "new-hrm-chip--red";
  }
  return "new-hrm-chip--blue";
};

const normalizeTimeStatusKey = (status) =>
  String(status || "").trim().toLowerCase().replace(/[\s_-]+/g, "");

const isRejectedStatus = (status) => normalizeTimeStatusKey(status) === "rejected";

const isEditableTimeStatus = (status) => {
  const normalizedStatus = normalizeTimeStatusKey(status);
  return !normalizedStatus || normalizedStatus === "draft" || normalizedStatus === "rejected";
};

const isLockedTimeStatus = (status) => !isEditableTimeStatus(status);

const isLeaveTimeItem = (item = {}) => {
  if (
    item.isLeaveEntry ||
    item.isLeaveTimeEntry ||
    item.leaveEntry ||
    item.leaveTimeEntry ||
    item.isLeave
  ) {
    return true;
  }

  const descriptor = String(`${item.description || ""} ${item.source || ""} ${item.entryType || ""}`)
    .trim()
    .toLowerCase();
  return (
    descriptor.includes("auto-created from approved leave") ||
    descriptor.includes("took leave") ||
    descriptor.includes("leave time entry")
  );
};

const isReadOnlyTimeItem = (item = {}, status = "", dayLocked = false) =>
  Boolean(dayLocked || isLeaveTimeItem(item) || item.lockRows || item.readOnly || item.canEdit === false || isLockedTimeStatus(status));

const REVIEW_COMMENT_KEYS = [
  "reviewComment",
  "reviewComments",
  "reviewNote",
  "reviewNotes",
  "rejectionReason",
  "rejectionComment",
  "rejectedReason",
  "managerComment",
  "approverComment",
  "approvalComment",
  "comments",
  "comment",
  "Review_Comment__c",
  "Review_Comments__c",
  "Rejection_Reason__c",
  "Rejection_Comment__c",
  "Approver_Comment__c",
  "Manager_Comment__c",
];

const normalizeReviewCommentValue = (value) => {
  if (value === undefined || value === null) {
    return "";
  }
  if (typeof value === "string" || typeof value === "number") {
    const cleanedText = String(value)
      .replace(/^\*+\s*/, "")
      .replace(/\s+,/g, ",")
      .replace(/,\s*/g, ", ")
      .trim();
    return /^[*._\-\s]+$/.test(cleanedText) ? "" : cleanedText;
  }
  if (Array.isArray(value)) {
    return value.map(normalizeReviewCommentValue).filter(Boolean).join(" ");
  }
  if (typeof value === "object") {
    for (const key of REVIEW_COMMENT_KEYS.concat(["body", "message", "note", "reason", "text", "value"])) {
      const normalizedValue = normalizeReviewCommentValue(value[key]);
      if (normalizedValue) {
        return normalizedValue;
      }
    }
  }
  return "";
};

const getReviewComment = (source) => {
  if (!source || typeof source !== "object") {
    return normalizeReviewCommentValue(source);
  }

  for (const key of REVIEW_COMMENT_KEYS) {
    const normalizedValue = normalizeReviewCommentValue(source[key]);
    if (normalizedValue) {
      return normalizedValue;
    }
  }

  return "";
};

const getSubmitHourIssues = (selectedTimesheetCellIds = [], monthTimesheet = [], selectedTimesheetMonth, selectedTimesheetMonthId) =>
  selectedTimesheetCellIds
    .filter((cellId) => !selectedTimesheetMonthId || cellId.startsWith(`${selectedTimesheetMonthId}:`))
    .map((cellId) => {
      const [, weekId, dayId] = cellId.split(":");
      const week = monthTimesheet.find((monthWeek) => monthWeek.id === weekId);
      const day = monthDays.find((monthDay) => monthDay.id === dayId);
      const entry = week?.entries?.[dayId];
      const status = getEntryStatus(entry, selectedTimesheetMonth);
      if (!week || !day || entry?.isOutsideMonth || !entry?.workDate || isLockedTimeStatus(status)) {
        return null;
      }
      const hours = getEntryHours(entry);
      return hours === REQUIRED_DAILY_TIMESHEET_HOURS ? null : `${week.label} ${day.label} (${Number(hours.toFixed(2)) || 0}h)`;
    })
    .filter(Boolean);

const createTimeListItem = (workDate = "") => ({
  id: `list-row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  rowKey: `list-row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  workDate,
  hours: "",
  project: "",
  projectId: "",
  task: "",
  taskId: "",
  description: "",
});

const getTimeItemKey = (item = {}) => item.rowKey || item.clientRowId || item.id;

const withDeletedTimeListItem = (entry = {}, item = {}) => {
  const itemStatus = item.status || entry.status || "Draft";
  if (!item || !Object.keys(item).length || isReadOnlyTimeItem(item, itemStatus)) {
    return entry;
  }

  return {
    ...entry,
    deletedEntries: [
      ...(Array.isArray(entry.deletedEntries) ? entry.deletedEntries : []),
      {
        ...item,
        workDate: item.workDate || entry.workDate || "",
        status: itemStatus,
      },
    ],
  };
};

const normalizeTimeListEntry = (entry = {}) => {
  const items = (entry.items || [])
    .filter(Boolean)
    .map((item) => {
      const fallbackItem = createTimeListItem(entry.workDate || item.workDate || "");
      return {
        id: item.id || fallbackItem.id,
        timeEntryId: item.timeEntryId || item.timeSheetEntryId || item.timesheetEntryId || item.timeCardEntryId || item.timecardEntryId || item.entryId || item.recordId || item.salesforceId || item.sfId || item.id || "",
        recordId: item.recordId || item.salesforceId || item.sfId || item.id || "",
        salesforceId: item.salesforceId || item.sfId || item.recordId || item.id || "",
        timeCardEntryId: item.timeCardEntryId || item.timecardEntryId || item.timeEntryId || item.id || "",
        rowKey: getTimeItemKey(item) || fallbackItem.rowKey,
        workDate: item.workDate || entry.workDate || "",
        hours: item.hours || "",
        project: item.project || item.projectName || "",
        projectId: item.projectId || "",
        projectColor: item.projectColor || item.projectColour || "",
        projectColour: item.projectColour || item.projectColor || "",
        task: item.taskTitle || item.title || item.task || item.taskName || "",
        taskId: item.taskId || item.entityId || "",
        description: item.description || item.Desc__c || "",
        approvedHours: item.approvedHours ?? item.approvedHoursValue ?? "",
        lockRows: Boolean(item.lockRows),
        isLeaveEntry: Boolean(item.isLeaveEntry || item.isLeaveTimeEntry || item.leaveEntry || item.leaveTimeEntry || item.isLeave),
        readOnly: Boolean(item.readOnly),
        canEdit: item.canEdit,
        canSubmit: item.canSubmit,
        reviewComment: getReviewComment(item),
        status: item.status || entry.status || "",
      };
    });

  return {
    ...entry,
    status: entry.status || "",
    deletedEntries: Array.isArray(entry.deletedEntries) ? entry.deletedEntries : [],
    reviewComment: getReviewComment(entry),
    items: items.length ? items : [createTimeListItem(entry.workDate || "")],
  };
};

const createTimeListDraft = (weeks = []) =>
  weeks.reduce((draft, week) => {
    draft[week.id] = monthDays.reduce((entries, day) => {
      entries[day.id] = normalizeTimeListEntry(week.entries?.[day.id] || {});
      return entries;
    }, {});
    return draft;
  }, {});

const hasTimeRowInput = (item = {}) =>
  Boolean(
    String(item.hours || "").trim() ||
      String(item.project || item.projectId || "").trim() ||
      String(item.task || item.taskId || "").trim() ||
      String(item.description || "").trim()
  );

const normalizeComparableText = (value) => String(value || "").trim().toLowerCase();

const getProjectOptionId = (project = {}) => String(project.id || project.projectId || project.Project__c || "").trim();

const getTaskProjectId = (task = {}) => String(task.projectId || task.Project__c || task.project?.id || task.Project__r?.Id || "").trim();

const getTaskProjectName = (task = {}) =>
  normalizeComparableText(task.projectName || task.projectTitle || task.project || task.Project__r?.Name || task.Project__r?.Title__c);

const findProjectOption = (projectOptions = [], item = {}) => {
  const projectId = String(item.projectId || "").trim();
  const projectName = normalizeComparableText(item.project || item.projectName);
  return projectOptions.find((project) => {
    const optionId = getProjectOptionId(project);
    return (projectId && optionId === projectId) || (projectName && normalizeComparableText(project.name) === projectName);
  });
};

const isTaskForProject = (task = {}, project = {}) => {
  const projectId = getProjectOptionId(project);
  const taskProjectId = getTaskProjectId(task);
  if (projectId && taskProjectId) {
    return projectId === taskProjectId;
  }

  const projectName = normalizeComparableText(project.name || project.projectName);
  const taskProjectName = getTaskProjectName(task);
  return Boolean(projectName && taskProjectName && projectName === taskProjectName);
};

const getTaskOptionsForProject = (project = {}, taskOptions = []) =>
  project ? taskOptions.filter((task) => isTaskForProject(task, project)) : [];

const findTaskOption = (taskOptions = [], taskName = "", project = {}) => {
  const cleanTaskName = normalizeComparableText(taskName);
  if (!cleanTaskName || !project) {
    return undefined;
  }
  return getTaskOptionsForProject(project, taskOptions).find((task) => normalizeComparableText(task.name) === cleanTaskName);
};

const getTaskDatalistId = (weekId, dayId, rowKey) =>
  `new-hrm-time-list-task-options-${weekId}-${dayId}-${rowKey}`.replace(/[^a-zA-Z0-9_-]/g, "-");

const isCompactTimesheetViewport = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(max-width: 920px)").matches;

const formatEntryDateLabel = (workDate) => {
  const date = new Date(`${workDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return workDate || "Date";
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    weekday: "long",
  });
};

const getDayEntryRows = (entry = {}) => (entry.items || []).filter(hasTimeRowInput);

const getDayEntrySummary = (entry = {}) => {
  const rows = getDayEntryRows(entry);
  if (!rows.length) {
    return "No time entered";
  }
  return rows.map((item) => `${item.project || "Project"}${item.task ? ` / ${item.task}` : ""}`).join(", ");
};

const TimeEntriesPanel = ({
  fullScreen = false,
  holidays = [],
  isTimesheetLocked,
  loading = false,
  loadingLabel = "Updating timesheet",
  monthTimesheet,
  onAction,
  onExpand,
  onRefreshTimesheet,
  onSubmitTime,
  onSubmitTimesheetApproval,
  onTimeCellEdit,
  onTimesheetCellToggle,
  onTimesheetDateSelect,
  onTimesheetMonthChange,
  selectedTimesheetCellIds = [],
  selectedTimesheetCellCount = 0,
  selectedTimesheetDate,
  selectedTimesheetMonth,
  selectedTimesheetMonthId,
  projectOptions = [],
  taskOptions = [],
  timesheetMonths = [],
  timesheetLockMessage,
  totalHours,
}) => {
  const lastTapRef = React.useRef({ cellId: "", time: 0 });
  const clickTimerRef = React.useRef(null);
  const [timeView, setTimeView] = React.useState("calendar");
  const [compactViewport, setCompactViewport] = React.useState(isCompactTimesheetViewport);
  const [openListDayIds, setOpenListDayIds] = React.useState(() => new Set());
  const [listDraft, setListDraft] = React.useState(() => createTimeListDraft(monthTimesheet));
  const [listError, setListError] = React.useState("");
  const resolvedTimeView = compactViewport ? "list" : timeView;
  const isApprovedMonth = String(selectedTimesheetMonth?.status || "").toLowerCase() === "approved";
  const monthStatus = isTimesheetLocked && !isApprovedMonth ? "Closed" : selectedTimesheetMonth?.status || "Draft";
  const submitHourIssues = getSubmitHourIssues(
    selectedTimesheetCellIds,
    monthTimesheet,
    selectedTimesheetMonth,
    selectedTimesheetMonthId
  );
  const hasSubmitHourIssue = submitHourIssues.length > 0;
  const submitHourMessage = hasSubmitHourIssue
    ? `Submit requires exactly ${REQUIRED_DAILY_TIMESHEET_HOURS} hours on each selected date. Fix ${submitHourIssues.slice(0, 3).join(", ")}${submitHourIssues.length > 3 ? "..." : ""}.`
    : "";
  const holidaysByDate = React.useMemo(
    () =>
      holidays.reduce((map, holiday) => {
        const dateKey = holiday.dateKey || toDateKey(holiday.rawDate || holiday.date);
        if (dateKey) {
          map.set(dateKey, holiday);
        }
        return map;
      }, new Map()),
    [holidays]
  );
  const todayDateKey = React.useMemo(() => getTodayDateKey(), []);
  const activeTimesheetDateKey = toDateKey(selectedTimesheetDate);
  const timeListDays = React.useMemo(
    () =>
      monthTimesheet.flatMap((week) =>
        monthDays
          .map((day) => {
            const entry = week.entries?.[day.id];
            if (entry?.isOutsideMonth || !entry?.workDate) {
              return null;
            }
            const draftEntry = listDraft[week.id]?.[day.id] || normalizeTimeListEntry(entry);
            const status = getEntryStatus(draftEntry, selectedTimesheetMonth);
            const workDate = draftEntry.workDate || entry.workDate;
            const isWeekend = isWeekendWorkDate(workDate);
            return {
              cellId: `${selectedTimesheetMonthId}:${week.id}:${day.id}`,
              day,
              dayId: day.id,
              draftEntry,
              entry,
              isLocked: isTimesheetLocked || isLockedTimeStatus(status),
              isWeekend,
              status,
              week,
              workDate,
            };
          })
          .filter(Boolean)
      ),
    [isTimesheetLocked, listDraft, monthTimesheet, selectedTimesheetMonth, selectedTimesheetMonthId]
  );

  React.useEffect(() => {
    setListDraft(createTimeListDraft(monthTimesheet));
    setListError("");
  }, [monthTimesheet]);

  React.useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(max-width: 920px)");
    const handleViewportChange = (event) => setCompactViewport(event.matches);
    setCompactViewport(mediaQuery.matches);
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleViewportChange);
      return () => mediaQuery.removeEventListener("change", handleViewportChange);
    }
    mediaQuery.addListener?.(handleViewportChange);
    return () => mediaQuery.removeListener?.(handleViewportChange);
  }, []);

  const updateListDayEntry = (weekId, dayId, updater) => {
    setListDraft((currentDraft) => {
      const currentWeekDraft = currentDraft[weekId] || {};
      const currentEntry = currentWeekDraft[dayId] || normalizeTimeListEntry(
        monthTimesheet.find((week) => week.id === weekId)?.entries?.[dayId] || {}
      );
      return {
        ...currentDraft,
        [weekId]: {
          ...currentWeekDraft,
          [dayId]: updater(currentEntry),
        },
      };
    });
  };

  const toggleListDay = (dayKey) => {
    setOpenListDayIds((current) => {
      const next = new Set(current);
      if (next.has(dayKey)) {
        next.delete(dayKey);
      } else {
        next.add(dayKey);
      }
      return next;
    });
  };

  const handleListItemChange = (weekId, dayId, itemKey, field, value) => {
    setListError("");
    updateListDayEntry(weekId, dayId, (entry) => {
      return {
        ...entry,
        items: (entry.items || [createTimeListItem(entry.workDate)]).map((item) => {
          if (getTimeItemKey(item) !== itemKey) {
            return item;
          }
          if (field === "project") {
            const selectedProject = findProjectOption(projectOptions, { project: value });
            const selectedTask = findTaskOption(taskOptions, item.task, selectedProject);
            return {
              ...item,
              project: value,
              projectId: selectedProject ? getProjectOptionId(selectedProject) : "",
              projectColor: selectedProject?.colorCode || selectedProject?.color || selectedProject?.colour || selectedProject?.projectColor || "",
              projectColour: selectedProject?.colorCode || selectedProject?.color || selectedProject?.colour || selectedProject?.projectColor || "",
              task: selectedTask ? item.task : "",
              taskId: selectedTask?.id || "",
            };
          }
          if (field === "task") {
            const selectedProject = findProjectOption(projectOptions, item);
            const selectedTask = findTaskOption(taskOptions, value, selectedProject);
            return {
              ...item,
              task: value,
              taskId: selectedTask?.id || "",
            };
          }
          return {
            ...item,
            [field]: value,
          };
        }),
      };
    });
  };

  const handleAddListItem = (weekId, dayId) => {
    setListError("");
    updateListDayEntry(weekId, dayId, (entry) => {
      return {
        ...entry,
        items: [...(entry.items || []), createTimeListItem(entry.workDate)],
      };
    });
  };

  const handleRemoveListItem = (weekId, dayId, itemKey) => {
    setListError("");
    updateListDayEntry(weekId, dayId, (entry) => {
      const removedItem = (entry.items || []).find((item) => getTimeItemKey(item) === itemKey);
      const entryWithDelete = withDeletedTimeListItem(entry, removedItem);
      const remainingItems = (entry.items || []).filter((item) => getTimeItemKey(item) !== itemKey);
      return {
        ...entryWithDelete,
        items: remainingItems.length ? remainingItems : [createTimeListItem(entry.workDate)],
      };
    });
  };

  const handleSaveListDay = async (week, dayId) => {
    const currentEntry = listDraft[week.id]?.[dayId] || normalizeTimeListEntry(week.entries?.[dayId] || {});
    const status = getEntryStatus(currentEntry, selectedTimesheetMonth);
    if (!onSubmitTime || loading || isTimesheetLocked || isLockedTimeStatus(status)) {
      return;
    }

    const populatedItems = (currentEntry.items || []).filter(hasTimeRowInput);
    const editableItems = populatedItems.filter((item) => !isReadOnlyTimeItem(item, item.status || status));
    const lockedHours = populatedItems
      .filter((item) => isReadOnlyTimeItem(item, item.status || status))
      .reduce((sum, item) => sum + Number(item.hours || 0), 0);
    const invalidItem = editableItems.find((item) => {
      const selectedProject = findProjectOption(projectOptions, item);
      const selectedTask = item.task ? findTaskOption(taskOptions, item.task, selectedProject) : undefined;
      const hours = Number(item.hours || 0);
      return (
        !selectedProject ||
        !String(item.project || "").trim() ||
        !String(item.description || "").trim() ||
        !Number.isFinite(hours) ||
        hours <= 0 ||
        hours > REQUIRED_DAILY_TIMESHEET_HOURS ||
        (String(item.task || "").trim() && !selectedTask)
      );
    });

    if (invalidItem) {
      setListError("Each row needs a valid project, project entity from that project if selected, description, and 1-8 hours.");
      return;
    }

    const totalDayHours = editableItems.reduce((sum, item) => sum + Number(item.hours || 0), lockedHours);
    if (totalDayHours > REQUIRED_DAILY_TIMESHEET_HOURS) {
      setListError(`Daily timesheet total cannot exceed ${REQUIRED_DAILY_TIMESHEET_HOURS} hours.`);
      return;
    }

    const normalizedItems = editableItems.map((item) => {
      const selectedProject = findProjectOption(projectOptions, item);
      const selectedTask = item.task ? findTaskOption(taskOptions, item.task, selectedProject) : undefined;
      return {
        ...item,
        project: selectedProject?.name || item.project,
        projectId: getProjectOptionId(selectedProject),
        task: selectedTask?.name || "",
        taskId: selectedTask?.id || "",
        status: "Draft",
      };
    });
    const nextWeekEntries = {
      ...(listDraft[week.id] || week.entries || {}),
      [dayId]: {
        ...currentEntry,
        status: "Draft",
        items: normalizedItems.length ? normalizedItems : [],
      },
    };
    setListError("");
    await onSubmitTime({ weekId: week.id, entries: nextWeekEntries });
  };
  const actionControls = (
    <>
      <div className="new-hrm-time-panel__utility-actions">
        <span className="new-hrm-time-panel__help" tabIndex={0} role="img" aria-label="Timesheet help" title={resolvedTimeView === "list" ? "Open a date row to review or update its entries." : "Select dates using the checkboxes. Double-click a day or use its edit button to open the entry."}>
          <FiHelpCircle aria-hidden="true" />
        </span>
        {!compactViewport && !fullScreen && onExpand && (
          <button
            className="new-hrm-icon-button new-hrm-time-panel__expand"
            type="button"
            onClick={onExpand}
            title="Open full time entries"
            aria-label="Open full time entries"
          >
            <FiMaximize2 aria-hidden="true" />
          </button>
        )}
        <button
          className="new-hrm-icon-button new-hrm-time-panel__refresh"
          type="button"
          onClick={onRefreshTimesheet}
          disabled={loading}
          title="Refresh timesheet"
          aria-label="Refresh timesheet"
        >
          <FiRefreshCw aria-hidden="true" />
        </button>
        <div className="new-hrm-view-toggle new-hrm-time-panel__view-toggle" role="group" aria-label="Timesheet view">
          {!compactViewport && (
            <button
              type="button"
              className={resolvedTimeView === "calendar" ? "new-hrm-view-toggle__item--active" : ""}
              onClick={() => setTimeView("calendar")}
              disabled={loading}
            >
              <FiGrid aria-hidden="true" />
              Calendar
            </button>
          )}
          <button
            type="button"
            className={resolvedTimeView === "list" ? "new-hrm-view-toggle__item--active" : ""}
            onClick={() => setTimeView("list")}
            disabled={loading}
          >
            <FiList aria-hidden="true" />
            List
          </button>
        </div>
        {fullScreen && (
          <button className="new-hrm-secondary-button" type="button" onClick={() => onAction("timesheet")} disabled={loading}>
            Timesheet
          </button>
        )}
      </div>
      <div className="new-hrm-time-panel__workflow-actions">
        <button className="new-hrm-primary-button" type="button" onClick={() => onTimesheetDateSelect(selectedTimesheetDate, true)} disabled={loading || isTimesheetLocked} aria-label="Log Selected Week">
          <span className="new-hrm-action-label--desktop">Log Selected Week</span>
          <span className="new-hrm-action-label--mobile">Log Week</span>
        </button>
        <button className="new-hrm-secondary-button" type="button" onClick={() => onAction("bulkTime")} disabled={loading || isTimesheetLocked || !selectedTimesheetCellCount} aria-label={`Fill Selected${selectedTimesheetCellCount ? ` (${selectedTimesheetCellCount})` : ""}`}>
          <span className="new-hrm-action-label--desktop">Fill Selected{selectedTimesheetCellCount ? ` (${selectedTimesheetCellCount})` : ""}</span>
          <span className="new-hrm-action-label--mobile">Fill{selectedTimesheetCellCount ? ` (${selectedTimesheetCellCount})` : ""}</span>
        </button>
        <button className="new-hrm-secondary-button" type="button" onClick={onSubmitTimesheetApproval} disabled={loading || isTimesheetLocked || !selectedTimesheetCellCount || hasSubmitHourIssue} aria-label={`Submit Selected${selectedTimesheetCellCount ? ` (${selectedTimesheetCellCount})` : ""}`}>
          <span className="new-hrm-action-label--desktop">Submit Selected{selectedTimesheetCellCount ? ` (${selectedTimesheetCellCount})` : ""}</span>
          <span className="new-hrm-action-label--mobile">Submit{selectedTimesheetCellCount ? ` (${selectedTimesheetCellCount})` : ""}</span>
        </button>
      </div>
    </>
  );
  const monthSelector = (
    <label className="new-hrm-time-panel__month">
      <select
        aria-label="Timesheet month"
        disabled={loading}
        value={selectedTimesheetMonthId}
        onChange={(event) => onTimesheetMonthChange(event.target.value)}
      >
        {timesheetMonths.map((month) => (
          <option key={month.id} value={month.id}>
            {month.label}
          </option>
        ))}
      </select>
    </label>
  );

  React.useEffect(
    () => () => {
      if (clickTimerRef.current) {
        window.clearTimeout(clickTimerRef.current);
      }
    },
    []
  );

  const cancelPendingCellToggle = () => {
    if (clickTimerRef.current) {
      window.clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
  };

  const openEntryEditor = (weekId, dayId, isOutsideMonth) => {
    cancelPendingCellToggle();
    if (!loading && !isOutsideMonth) {
      onTimeCellEdit(weekId, dayId);
    }
  };

  const handleCellClick = (weekId, dayId, isLocked, status, isOutsideMonth) => {
    if (loading || isOutsideMonth) {
      return;
    }

    if (isLocked) {
      openEntryEditor(weekId, dayId, false);
      return;
    }

    if (isRejectedStatus(status)) {
      openEntryEditor(weekId, dayId, false);
      return;
    }

    cancelPendingCellToggle();
    clickTimerRef.current = window.setTimeout(() => {
      onTimesheetCellToggle(weekId, dayId);
      clickTimerRef.current = null;
    }, 340);
  };

  const handleCellTouchEnd = (event, weekId, dayId, cellId, isOutsideMonth) => {
    const now = Date.now();
    const lastTap = lastTapRef.current;
    if (lastTap.cellId === cellId && now - lastTap.time < 360) {
      event.preventDefault();
      cancelPendingCellToggle();
      lastTapRef.current = { cellId: "", time: 0 };
      openEntryEditor(weekId, dayId, isOutsideMonth);
      return;
    }
    lastTapRef.current = { cellId, time: now };
  };

  return (
    <section className={`new-hrm-panel new-hrm-time-panel new-hrm-loader-host ${fullScreen ? "new-hrm-time-panel--full" : ""}`}>
      <ComponentLoader active={loading} label={loadingLabel} />
      {fullScreen ? (
        <header className="new-hrm-time-panel__toolbar">
          {monthSelector}
          <div className="new-hrm-time-panel__actions">
            {actionControls}
          </div>
        </header>
      ) : (
        <header className="new-hrm-panel__header">
          <div>
            <h2>Time Entries</h2>
            <p>{selectedTimesheetMonth?.label || "Month"} Total Logged</p>
            <strong>{totalHours}</strong>
            <span className={`new-hrm-chip ${getStatusChipClass(monthStatus)}`}>
              {getStatusLabel(monthStatus)}
            </span>
          </div>
          <div className="new-hrm-time-panel__actions">
            {monthSelector}
            {actionControls}
          </div>
        </header>
      )}

      <div className="new-hrm-time-panel__notices">
        {isTimesheetLocked && (
          <p className="new-hrm-timesheet-lock-note">
            {timesheetLockMessage || `${selectedTimesheetMonth?.label} cannot be edited.`}
          </p>
        )}

        {submitHourMessage && (
          <p className="new-hrm-week-log-error" role="alert">
            {submitHourMessage}
          </p>
        )}

        {resolvedTimeView === "list" && listError && (
          <p className="new-hrm-week-log-error" role="alert">
            {listError}
          </p>
        )}
      </div>

      {resolvedTimeView === "list" ? (
        <div className="new-hrm-time-list" aria-label={`${selectedTimesheetMonth?.label || "Month"} timesheet list`}>
          <datalist id="new-hrm-time-list-project-options">
            {projectOptions.map((project) => (
              <option key={project.id} value={project.name} />
            ))}
          </datalist>
          {timeListDays.map(({ cellId, dayId, draftEntry, isLocked, isWeekend, status, week, workDate }) => {
            const isOpen = openListDayIds.has(cellId);
            const selected = selectedTimesheetCellIds.includes(cellId);
            const entryHours = getEntryHours(draftEntry);
            const rows = draftEntry.items || [createTimeListItem(workDate)];
            return (
              <article className={`new-hrm-time-list-day ${isOpen ? "new-hrm-time-list-day--open" : ""} ${isWeekend ? "new-hrm-time-list-day--weekend" : ""}`} key={cellId}>
                <button className="new-hrm-time-list-day__summary" type="button" onClick={() => toggleListDay(cellId)} aria-expanded={isOpen}>
                  <span className="new-hrm-time-list-day__date">
                    <strong>{formatEntryDateLabel(workDate)}</strong>
                    <small>{isWeekend ? `Weekend · ${week.range}` : `${week.label} · ${week.range}`}</small>
                  </span>
                  <span className="new-hrm-time-list-day__work">
                    <strong>{entryHours ? `${Number(entryHours.toFixed(2))}h` : "-"}</strong>
                    <small>{getDayEntrySummary(draftEntry)}</small>
                  </span>
                  <span className={`new-hrm-chip ${getStatusChipClass(status)}`}>
                    {getStatusLabel(status)}
                  </span>
                  <FiChevronDown aria-hidden="true" />
                </button>
                {isOpen && (
                  <div className="new-hrm-time-list-day__body">
                    <div className="new-hrm-time-list-day__actions">
                      <button
                        className="new-hrm-secondary-button"
                        type="button"
                        onClick={() => onTimesheetCellToggle(week.id, dayId)}
                        disabled={loading || isLocked}
                      >
                        {selected ? "Unselect date" : "Select date"}
                      </button>
                      <button
                        className="new-hrm-secondary-button"
                        type="button"
                        onClick={() => handleAddListItem(week.id, dayId)}
                        disabled={loading || isLocked}
                      >
                        <FiPlus aria-hidden="true" />
                        Add Row
                      </button>
                      <button
                        className="new-hrm-secondary-button"
                        type="button"
                        onClick={() => onTimeCellEdit(week.id, dayId)}
                        disabled={loading}
                      >
                        {isLocked ? "View popup" : "Open popup"}
                      </button>
                    </div>
                    <div className="new-hrm-time-list-rows">
                      {rows.map((item, index) => {
                        const itemKey = getTimeItemKey(item);
                        const itemStatus = item.status || status;
                        const isItemLocked = loading || isReadOnlyTimeItem(item, itemStatus, isLocked);
                        const selectedProject = findProjectOption(projectOptions, item);
                        const itemTaskOptions = getTaskOptionsForProject(selectedProject, taskOptions);
                        const taskDatalistId = getTaskDatalistId(week.id, dayId, itemKey || index);
                        const itemReviewComment = isRejectedStatus(itemStatus) ? getReviewComment(item) : "";
                        return (
                          <div className="new-hrm-time-list-row" key={itemKey || `${cellId}-${index}`}>
                            {getEntryProjectColor({ items: [item] }) && (
                              <span
                                className="new-hrm-time-list-row__color"
                                style={{ "--entry-project-color": getEntryProjectColor({ items: [item] }) }}
                                aria-hidden="true"
                              />
                            )}
                            <label className="new-hrm-field">
                              <span>Project</span>
                              <input
                                list="new-hrm-time-list-project-options"
                                value={item.project || ""}
                                disabled={isItemLocked}
                                onChange={(event) => handleListItemChange(week.id, dayId, itemKey, "project", event.target.value)}
                                placeholder="Search project"
                              />
                            </label>
                            <label className="new-hrm-field">
                              <span>Project Entity</span>
                              <input
                                list={taskDatalistId}
                                value={item.task || ""}
                                disabled={isItemLocked || !selectedProject}
                                onChange={(event) => handleListItemChange(week.id, dayId, itemKey, "task", event.target.value)}
                                placeholder={selectedProject ? "Search entity" : "Select project first"}
                              />
                              <datalist id={taskDatalistId}>
                                {itemTaskOptions.map((task) => (
                                  <option key={task.id} value={task.name} />
                                ))}
                              </datalist>
                            </label>
                            <label className="new-hrm-field new-hrm-time-list-row__description">
                              <span>Description</span>
                              <textarea
                                rows="2"
                                value={item.description || ""}
                                disabled={isItemLocked}
                                onChange={(event) => handleListItemChange(week.id, dayId, itemKey, "description", event.target.value)}
                                placeholder="Work details"
                              />
                            </label>
                            <label className="new-hrm-field">
                              <span>Hours</span>
                              <input
                                type="number"
                                min="0.25"
                                max={REQUIRED_DAILY_TIMESHEET_HOURS}
                                step="0.25"
                                value={item.hours || ""}
                                disabled={isItemLocked}
                                onChange={(event) => handleListItemChange(week.id, dayId, itemKey, "hours", event.target.value)}
                              />
                            </label>
                            <button
                              className="new-hrm-week-work-row__remove"
                              type="button"
                              onClick={() => handleRemoveListItem(week.id, dayId, itemKey)}
                              disabled={isItemLocked}
                              aria-label={`Delete row ${index + 1} for ${formatEntryDateLabel(workDate)}`}
                            >
                              <FiTrash2 aria-hidden="true" />
                            </button>
                            {itemReviewComment && (
                              <div className="new-hrm-time-list-row__review" role="note">
                                <strong>TSE rejection reason</strong>
                                <p>{itemReviewComment}</p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="new-hrm-time-list-day__footer">
                      <span>{Number(entryHours.toFixed(2)) || 0} / {REQUIRED_DAILY_TIMESHEET_HOURS} hours</span>
                      <button
                        className="new-hrm-primary-button"
                        type="button"
                        onClick={() => handleSaveListDay(week, dayId)}
                        disabled={loading || isLocked || !onSubmitTime}
                      >
                        <FiSave aria-hidden="true" />
                        Save Date
                      </button>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      ) : (
      <div className="new-hrm-month-timesheet" aria-label={`${selectedTimesheetMonth?.label || "Month"} timesheet`}>
        <div className="new-hrm-month-timesheet__grid">
          <div className="new-hrm-month-timesheet__head"></div>
          {monthDays.map((day) => (
            <div key={day.id} className="new-hrm-month-timesheet__head">
              <abbr title={day.label}>{day.label.slice(0, 3)}</abbr>
            </div>
          ))}

          {monthTimesheet.map((week) => (
            <React.Fragment key={week.id}>
              <div className="new-hrm-month-timesheet__week">
                <strong>{week.label}</strong>
                <span>{week.range}</span>
              </div>
              {monthDays.map((day) => {
                const entry = week.entries[day.id];
                const status = getEntryStatus(entry, selectedTimesheetMonth);
                const cellId = `${selectedTimesheetMonthId}:${week.id}:${day.id}`;
                const isOutsideMonth = entry?.isOutsideMonth || !entry?.workDate;
                const entryDateKey = toDateKey(entry?.workDate);
                const isWeekend = !isOutsideMonth && isWeekendWorkDate(entry?.workDate);
                const isSelected = selectedTimesheetCellIds.includes(cellId);
                const isToday = !isOutsideMonth && entryDateKey === todayDateKey;
                const isActiveDate = !isOutsideMonth && activeTimesheetDateKey && entryDateKey === activeTimesheetDateKey;
                const isLocked = isTimesheetLocked || isLockedTimeStatus(status) || isOutsideMonth;
                const isViewOnly = isLocked && !isOutsideMonth;
                const holiday = isOutsideMonth ? null : holidaysByDate.get(toDateKey(entry?.workDate));
                return (
                  <div
                    key={`${week.id}-${day.id}`}
                    className={`new-hrm-month-timesheet__cell new-hrm-month-timesheet__cell--${isOutsideMonth ? "outside" : status.toLowerCase()} ${isSelected ? "new-hrm-month-timesheet__cell--selected" : ""} ${isLocked ? "new-hrm-month-timesheet__cell--locked" : ""} ${isViewOnly ? "new-hrm-month-timesheet__cell--view-only" : ""} ${holiday ? "new-hrm-month-timesheet__cell--holiday" : ""} ${isWeekend ? "new-hrm-month-timesheet__cell--weekend" : ""} ${isToday ? "new-hrm-month-timesheet__cell--today" : ""} ${isActiveDate ? "new-hrm-month-timesheet__cell--active-date" : ""}`}
                    role="button"
                    tabIndex={loading || isOutsideMonth ? -1 : 0}
                    onClick={() => handleCellClick(week.id, day.id, isLocked, status, isOutsideMonth)}
                    onDoubleClick={(event) => {
                      event.preventDefault();
                      cancelPendingCellToggle();
                      openEntryEditor(week.id, day.id, isOutsideMonth);
                    }}
                    onKeyDown={(event) => {
                      if (event.target !== event.currentTarget) return;
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        if (isRejectedStatus(status)) {
                          openEntryEditor(week.id, day.id, isOutsideMonth);
                        } else if (!loading && !isLocked) {
                          onTimesheetCellToggle(week.id, day.id);
                        } else {
                          openEntryEditor(week.id, day.id, isOutsideMonth);
                        }
                      }
                    }}
                    onTouchEnd={(event) => handleCellTouchEnd(event, week.id, day.id, cellId, isOutsideMonth)}
                    aria-pressed={isSelected}
                    aria-label={
                      isOutsideMonth
                        ? `${day.label} is outside ${selectedTimesheetMonth?.label || "selected month"}`
                        : isViewOnly
                        ? `View ${day.label} ${week.label} ${getStatusLabel(status)} timesheet box${holiday ? `, ${holiday.name}` : ""}`
                        : isLocked
                        ? `${day.label} ${week.label} timesheet box locked${holiday ? `, ${holiday.name}` : ""}`
                        : `Select ${day.label} ${week.label} ${getStatusLabel(status)} timesheet box${holiday ? `, ${holiday.name}` : ""}`
                    }
                  >
                    {!isOutsideMonth && (
                      <>
                        <span className="new-hrm-month-timesheet__cell-top">
                          <span className="new-hrm-month-timesheet__date">{formatWorkDateDay(entry)}</span>
                          {!isLocked && (
                            <input
                              className="new-hrm-month-timesheet__select"
                              type="checkbox"
                              aria-label={`Select ${entry.workDate}`}
                              checked={isSelected}
                              disabled={loading}
                              onClick={(event) => event.stopPropagation()}
                              onDoubleClick={(event) => event.stopPropagation()}
                              onTouchEnd={(event) => event.stopPropagation()}
                              onChange={() => {
                                cancelPendingCellToggle();
                                onTimesheetCellToggle(week.id, day.id);
                              }}
                            />
                          )}
                        </span>
                        <span className="new-hrm-month-timesheet__cell-main">
                          <small>{formatStatusWithHours(status, entry)}</small>
                          <button
                            type="button"
                            disabled={loading}
                            onClick={(event) => {
                              event.stopPropagation();
                              openEntryEditor(week.id, day.id, isOutsideMonth);
                            }}
                            onDoubleClick={(event) => event.stopPropagation()}
                            onTouchEnd={(event) => event.stopPropagation()}
                            title={`${isLocked ? "View" : "Edit"} ${entry.workDate}`}
                            aria-label={`${isLocked ? "View" : "Edit"} ${day.label} time entries for ${week.label}`}
                          >
                            {isWeekend ? <FiEdit3 aria-hidden="true" /> : isLocked ? <FiEye aria-hidden="true" /> : <FiEdit3 aria-hidden="true" />}
                          </button>
                        </span>
                        {holiday && <span className="new-hrm-month-timesheet__holiday" title={holiday.name}>{holiday.name || "Holiday"}</span>}
                      </>
                    )}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
      )}
    </section>
  );
};

export default TimeEntriesPanel;
